import type { DiagramError } from '@aha-app/diagrams';
import { ApplicationView } from '@aha-app/mvc';
import React, { useEffect, useRef } from 'react';
import type SmartObject from '../../objects/SmartObject';
import useSubscription from '../hooks/useSubscription';
import type { editor } from 'monaco-editor';
import type { MonacoEditor } from './DiagramLanguageProvider';

interface ObjectTextAreaProps {
  monaco: MonacoEditor;
  selectedObject: SmartObject;
}

const ObjectTextArea: React.FC<ObjectTextAreaProps> = ({
  monaco,
  selectedObject,
}) => {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editor = useRef<editor.IStandaloneCodeEditor>();

  const selectText = (event: CustomEvent) => {
    const { location, smartObjectId } = event.detail;

    if (smartObjectId === selectedObject.id && editor.current) {
      editor.current.focus();
      editor.current.setSelection({
        startLineNumber: location.startLine,
        startColumn: location.startColumn,
        endLineNumber: location.endLine,
        endColumn: location.endColumn,
      });
    }
  };

  const updateText = () => {
    if (editor.current) {
      const model = editor.current.getModel();
      if (!model) return;

      // Update all of the text with undo/redo.
      editor.current.pushUndoStop();
      editor.current.setSelection(new monaco.Selection(0, 0, 0, 0));
      editor.current.executeEdits('smart-object', [
        {
          range: model.getFullModelRange(),
          text: selectedObject.textContent,
        },
      ]);
      editor.current.pushUndoStop();
    }
  };

  useEffect(() => {
    if (editorContainerRef.current) {
      // Mount the editor.
      const newEditor = monaco.editor.create(editorContainerRef.current, {
        fixedOverflowWidgets: true,
        automaticLayout: true,
        lineNumbers: 'on',
        folding: false,
        wordWrap: 'on',
        wrappingIndent: 'deepIndent',
        tabSize: 2,
        insertSpaces: true,
        minimap: { enabled: false },
        value: selectedObject.textContent || 'cycle\n\nOne',
        language: 'ahaDiagrams',
        theme: 'ahaDiagramsTheme',
      });
      newEditor.onDidChangeModelContent(() => {
        const value = newEditor.getValue();
        selectedObject.set('textContent', value);
        // Clear the markers until we revalidate.
        monaco.editor.setModelMarkers(
          monaco.editor.getModels()[0],
          'owner',
          []
        );
      });
      newEditor.focus();
      editor.current = newEditor;

      if (selectedObject.diagram) {
        showErrors(selectedObject.diagram.errors);
      }

      return () => {
        if (editor.current) {
          editor.current.dispose();
        }
      };
    }
  }, [selectedObject]);

  const showErrors = (errors: DiagramError[]) => {
    const markers: editor.IMarkerData[] = [];
    errors.forEach(error => {
      markers.push({
        message: error.message,
        severity: monaco.MarkerSeverity.Error,
        startLineNumber: error.location?.startLine ?? 1,
        startColumn: error.location?.startColumn ?? 1,
        endLineNumber: error.location?.endLine ?? 1,
        endColumn: error.location?.endColumn ?? 100,
      });
    });

    monaco.editor.setModelMarkers(
      monaco.editor.getModels()[0],
      'owner',
      markers
    );
  };

  const receiveErrors = (event: CustomEvent) => {
    const { errors, smartObjectId } = event.detail;

    if (smartObjectId === selectedObject.id) {
      showErrors(errors);
    }
  };

  useSubscription('smartObject.clicked', selectText);
  useSubscription('smartObject.error', receiveErrors);
  useSubscription('smartObject.updated', updateText);

  return (
    <div
      ref={editorContainerRef}
      style={{
        height: 'calc(100% - var(--side-panel--content-padding))',
        borderTop: '1px solid var(--theme-light-border)',
        marginLeft: 'calc(-1 * var(--side-panel--content-padding))',
        marginRight: 'calc(-1 * var(--side-panel--content-padding))',
      }}
    />
  );
};

export default ApplicationView(ObjectTextArea);
